<template>
  <div v-if="i18nReady" class="login-page d-flex min-vh-100">
    <aside class="left-pane d-none d-lg-flex align-items-center justify-content-center p-5">
      <div class="hero text-center">
        <h1 class="hero-brand mb-4">BEST GROUP</h1>

        <div class="hero-subtitle-wrap">
          <p class="hero-subtitle mb-1">DIAMOND JEWELRY</p>
          <p class="hero-subtitle mb-0">MANUFACTURER</p>
        </div>
      </div>
    </aside>

    <main class="right-pane d-flex align-items-center justify-content-center w-100">
      <div class="card form-card bg-white">
        <div class="mobile-hero d-lg-none text-center mb-4">
          <h1 class="mobile-brand mb-2">BEST GROUP</h1>
          <p class="mobile-subtitle mb-0">DIAMOND JEWELRY</p>
          <p class="mobile-subtitle mb-0">MANUFACTURER</p>
        </div>

        <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
          <h2 class="auth-title mb-0">{{ $t('login.auth.loginTitle') }}</h2>

          <div class="lang-select">
            <label class="form-label m-0 me-2 visually-hidden" for="lang">
              {{ $t('login.auth.languageLabel') }}
            </label>
            <select id="lang" name="lang" class="form-select" v-model="language"
              :aria-label="$t('login.auth.languageAria')" autocomplete="language" @change="onLanguageChange">
              <option v-for="opt in langOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <form @submit.prevent="login" novalidate>
          <div class="mb-3">
            <label for="email" class="form-label">{{ $t('login.auth.email') }}</label>
            <input id="email" name="email" v-model.trim="email" type="email" class="form-control" inputmode="email"
              autocomplete="username email" required :placeholder="$t('login.auth.emailPlaceholder')" />
          </div>

          <div class="mb-3">
            <label for="password" class="form-label">{{ $t('login.auth.password') }}</label>
            <input id="password" name="password" v-model="password" type="password" class="form-control"
              autocomplete="current-password" required :placeholder="$t('login.auth.passwordPlaceholder')" />
          </div>

          <div class="d-flex justify-content-between align-items-center mb-4">
            <router-link :to="forgotRoute" class="link-secondary">
              {{ $t('login.auth.forgot') }}
            </router-link>
          </div>

          <button :disabled="loading" type="submit" id="login-button" name="login-button"
            class="btn btn-brand w-100 py-2 text-white">
            <span v-if="!loading">{{ $t('login.auth.login') }}</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-if="loading" class="ms-2">...</span>
          </button>

          <p v-if="error" class="text-danger small mt-3 mb-0" role="alert">{{ error }}</p>
        </form>

        <p class="legal-foot small mt-4 mb-0">
          {{ $t('login.legal.foot') }}
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import { fetchLocaleOptions, loadViewLocale, toBCP47 } from '../i18n/index.js'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'

export default {
  name: 'LoginView',
  data() {
    return {
      settings: useSettingsStore(),
      i18nReady: false,
      email: '',
      password: '',
      remember: false,
      loading: false,
      error: '',
      language: 'pl-PL',
      langOptions: [],
    }
  },
  computed: {
    forgotRoute() {
      return {
        path: '/forgot-password',
        query: this.language ? { lang: this.language } : {},
      }
    },
  },
  watch: {
    '$route.query.lang': {
      async handler(val) {
        const lang = this.normalizeLangParam(val)
        if (!lang || lang === this.settings.locale) return
        await this.setAppLanguage(lang, { syncQuery: false })
      },
    },
  },
  async mounted() {
    await this.initLanguage()
    this.i18nReady = true
  },
  methods: {
    normalizeLangParam(raw) {
      const v = String(raw || '').trim()
      return v ? toBCP47(v) : ''
    },

    async ensureLocaleOptions() {
      if (!this.settings.localeOptions?.length) {
        await this.settings.init(this.settings.locale || 'pl-PL')
      }

      this.langOptions = this.settings.localeOptions?.length
        ? this.settings.localeOptions
        : await fetchLocaleOptions(true)
    },

    async setAppLanguage(next, { syncQuery = true } = {}) {
      const normalized = toBCP47(next || 'pl-PL')

      await this.settings.changeLocale(normalized)
      await loadViewLocale('LoginView', this.settings.locale)

      this.language = this.settings.locale
      await this.ensureLocaleOptions()

      if (syncQuery) {
        await this.syncLangQuery()
      }
    },

    async initLanguage() {
      await this.ensureLocaleOptions()

      const routeLang = this.normalizeLangParam(this.$route?.query?.lang)
      const savedLang = localStorage.getItem('sv-lang') || this.settings.locale || this.language
      const preferred = routeLang || savedLang

      await this.setAppLanguage(preferred, { syncQuery: true })
    },

    async syncLangQuery() {
      const current = this.normalizeLangParam(this.$route?.query?.lang)
      if (current === this.language) return

      try {
        await this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, lang: this.language },
        })
      } catch { }
    },

    async onLanguageChange(evt) {
      const val = evt?.target?.value ?? this.language
      await this.setAppLanguage(val, { syncQuery: true })
    },

    async login() {
      this.loading = true
      this.error = ''

      try {
        await axios.get('/api/auth/csrf')

        const res = await axios.post(
          '/api/auth/login',
          {
            email: this.email,
            password: this.password,
            lang: this.language,
          },
          { headers: { 'Accept-Language': this.language } }
        )

        const { roles } = res.data || {}

        const auth = useAuthStore()
        auth.setProfile(res.data)

        if (this.remember) localStorage.setItem('remember-email-B2B', this.email)
        else localStorage.removeItem('remember-email-B2B')

        const roleNames = (Array.isArray(roles) ? roles : [])
          .map(r => (typeof r === 'string' ? r : r?.name))
          .filter(Boolean)
          .map(r => r.toUpperCase())

        const isEmployee = roleNames.includes('PRACOWNIK')
        this.$router.push(isEmployee ? '/shop' : '/shop')
      } catch (e) {
        console.error(e)
        this.error = e?.response?.data?.error || this.$t('login.auth.error')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
<style scoped>
.login-page {
  --bg-brown: #2a100a;
  --bg-grey: #c3c2c4;
  --bg-black: #20201e;
  --bg-white: #ffffff;
  --hero-text: #f4f4f4;
  --muted-text: rgba(32, 32, 30, 0.72);
  --input-border: rgba(32, 32, 30, 0.16);
  --input-border-focus: rgba(42, 16, 10, 0.42);
  --card-shadow: 0 24px 48px rgba(32, 32, 30, 0.12);

  background: var(--bg-grey);
  font-family: "Inter", sans-serif;
  color: var(--bg-black);
}

.left-pane {
  flex: 0 0 50%;
  background: var(--bg-brown);
  color: var(--hero-text);
  position: relative;
  overflow: hidden;
}

.hero {
  width: 100%;
  max-width: 640px;
}

.hero-brand {
  margin: 0;
  font-family: "Clash Display", "Inter", sans-serif;
  font-weight: 600;
  font-size: clamp(2.6rem, 4vw, 4.1rem);
  line-height: 1;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--hero-text);
}

.hero-subtitle-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.12rem;
}

.hero-subtitle {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 1.05rem;
  line-height: 1.25;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
}

.right-pane {
  flex: 1 1 auto;
  background: var(--bg-grey);
  padding: 2rem;
}

.form-card {
  width: 100%;
  max-width: 520px;
  border: 0;
  border-radius: 0;
  background: var(--bg-white);
  box-shadow: var(--card-shadow);
  padding: 2.5rem !important;
}

.auth-title {
  font-family: "Clash Display", "Inter", sans-serif;
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 1.1;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--bg-black);
}

.form-label {
  font-family: "Inter", sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--bg-black);
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  min-height: 50px;
  border-radius: 0;
  border: 1px solid var(--input-border);
  background: var(--bg-white);
  color: var(--bg-black);
  font-family: "Inter", sans-serif;
  font-size: 0.95rem;
  box-shadow: none;
}

.form-control::placeholder {
  color: rgba(32, 32, 30, 0.45);
}

.form-control:focus,
.form-select:focus {
  border-color: var(--input-border-focus);
  box-shadow: 0 0 0 0.18rem rgba(42, 16, 10, 0.08);
}

.btn-brand {
  min-height: 52px;
  border-radius: 0;
  border: 1px solid var(--bg-brown);
  background: var(--bg-brown);
  color: var(--bg-white);
  font-family: "Inter", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.btn-brand:hover:enabled,
.btn-brand:focus:enabled {
  background: #3a1710;
  border-color: #3a1710;
  color: var(--bg-white);
}

.btn-brand:active:enabled {
  transform: translateY(1px);
}

.link-secondary {
  font-family: "Inter", sans-serif;
  font-size: 0.92rem;
  color: var(--muted-text) !important;
  text-decoration: none;
}

.link-secondary:hover {
  color: var(--bg-brown) !important;
  text-decoration: underline;
}

.legal-foot {
  font-family: "Inter", sans-serif;
  color: var(--muted-text);
}

.lang-select {
  min-width: 170px;
}

.mobile-hero {
  color: var(--bg-brown);
}

.mobile-brand {
  margin: 0;
  font-family: "Clash Display", "Inter", sans-serif;
  font-weight: 600;
  font-size: 1.9rem;
  line-height: 1;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--bg-brown);
}

.mobile-subtitle {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 0.76rem;
  line-height: 1.25;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--bg-brown);
}

.visually-hidden {
  position: absolute !important;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}

.login-page,
.right-pane,
.form-card {
  color-scheme: light;
}

@media (max-width: 991.98px) {
  .right-pane {
    padding: 1rem;
  }

  .form-card {
    max-width: 100%;
    padding: 1.5rem !important;
  }
}

@media (max-width: 575.98px) {
  .mobile-brand {
    font-size: 1.5rem;
    letter-spacing: 0.14em;
  }

  .mobile-subtitle {
    font-size: 0.7rem;
    letter-spacing: 0.16em;
  }

  .auth-title {
    font-size: 1.15rem;
  }
}
</style>